import { type JSX, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import isEqual from "lodash/isEqual";

import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { type KeywordListEntity, useKeywordLists } from "app/modules/demand-analysis/keyword-lists";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { DemandAnalysisSearchTrendsQueryParams } from "../../types";

import { KeywordListsComparisonNavQueryBarItem } from "./KeywordListsComparisonNavQueryBarItem";

/**
 * Synchronizes keyword lists state to URL parameters when user finishes interacting.
 * This ensures URL reflects current keyword list selection for deep-linking and browser navigation.
 *
 * @param activeItemId - ID of currently active/selected item, null when no item is being interacted with
 */
const useSyncStateToURL = (activeItemId: string | null) => {
    const { params } = useCurrentStateAndParams<DemandAnalysisSearchTrendsQueryParams>();
    const { keywordLists } = useKeywordLists();

    useEffect(() => {
        const nextParams: DemandAnalysisSearchTrendsQueryParams = {
            ...params,
            id: keywordLists.map((keywordList) => keywordList.keywordListId),
        };

        if (!isEqual(nextParams, params)) {
            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, nextParams, {
                inherit: false,
                reload: false,
            });
        }
    }, [activeItemId, keywordLists, params]);
};

export const KeywordListsComparisonNavQueryBar = ({ limit = 5 }: { limit?: number }): JSX.Element => {
    const { keywordLists, addKeywordList, replaceKeywordList, removeKeywordList } = useKeywordLists();
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [primaryKeywordList, ...restKeywordLists] = keywordLists;
    const isAddingAllowed = keywordLists.length < limit;
    const selectedKeywordListIds = keywordLists.map((keywordList) => keywordList.id);

    const handleKeywordListAdd = (entity: KeywordListEntity) => {
        addKeywordList(entity);
    };

    const handleKeywordListChange = (prevEntity: KeywordListEntity, nextEntity: KeywordListEntity) => {
        replaceKeywordList(prevEntity, nextEntity);
        setActiveItemId(null);
    };

    const handleKeywordListRemove = (entity: KeywordListEntity) => {
        removeKeywordList(entity);
        setActiveItemId(null);
    };

    const handleOpen = (id: string) => {
        setActiveItemId(id);
    };

    const handleClose = () => {
        setActiveItemId(null);
    };

    useSyncStateToURL(activeItemId);

    return (
        <Flex
            paddingX="16px"
            alignItems="center"
            flexWrap="nowrap"
        >
            <KeywordListsComparisonNavQueryBarItem
                isActive={activeItemId === primaryKeywordList.id}
                currentKeywordList={primaryKeywordList}
                selectedKeywordListIds={selectedKeywordListIds}
                onKeywordListAdd={handleKeywordListAdd}
                onKeywordListChange={handleKeywordListChange}
                onKeywordListRemove={handleKeywordListRemove}
                onOpen={handleOpen}
                onClose={handleClose}
            />
            {restKeywordLists.length > 0 ? (
                <Flex
                    alignItems="center"
                    marginX="8px"
                >
                    <Text fontVariant="body1">{translate("common.vs")}</Text>
                </Flex>
            ) : null}
            <Flex
                gap="16px"
                flexWrap="nowrap"
            >
                {restKeywordLists.map((keywordList) => (
                    <KeywordListsComparisonNavQueryBarItem
                        key={keywordList.id}
                        isActive={activeItemId === keywordList.id}
                        currentKeywordList={keywordList}
                        selectedKeywordListIds={selectedKeywordListIds}
                        onKeywordListAdd={handleKeywordListAdd}
                        onKeywordListChange={handleKeywordListChange}
                        onKeywordListRemove={handleKeywordListRemove}
                        onOpen={handleOpen}
                        onClose={handleClose}
                    />
                ))}
            </Flex>
            {isAddingAllowed ? (
                <Box margin="0 0 0 12px">
                    <KeywordListsComparisonNavQueryBarItem
                        key={selectedKeywordListIds.join(";")}
                        isActive={activeItemId === KeywordListsComparisonNavQueryBarItem.autoCompleteId}
                        selectedKeywordListIds={selectedKeywordListIds}
                        onKeywordListAdd={handleKeywordListAdd}
                        onKeywordListChange={handleKeywordListChange}
                        onKeywordListRemove={handleKeywordListRemove}
                        onOpen={handleOpen}
                        onClose={handleClose}
                    />
                </Box>
            ) : null}
        </Flex>
    );
};
